import { EffectsMap } from 'use-effect-reducer'
import { fetchCNPConfig } from '../../helpers/fetchCNPConfig'
import {
  LSPAY_ERROR,
  LSPAY_ERROR_MESSAGES,
  FAILED_CNP_CONFIG,
  FAILED_INTENT,
  FAILED_CAPTURE,
  SWIPER_ERROR_CODES,
  LSPAY_STEPS,
} from '../../helpers/lspayConstants'
import {
  capturePayment,
  createPaymentIntent,
} from '../../helpers/paymentIntent'
import {
  CardNotPresentPaymentEffect,
  CardNotPresentPaymentEffectsMapArgs,
  CardNotPresentPaymentEvent,
  CardNotPresentPaymentState,
} from './types'

export const getCardNotPresentPaymentEffectsMap = (
  args: CardNotPresentPaymentEffectsMapArgs
): EffectsMap<
  CardNotPresentPaymentState,
  CardNotPresentPaymentEvent,
  CardNotPresentPaymentEffect
> => {
  const {
    outletId,
    currency,
    amount,
    retailerPaymentTypeId,
    register,
    transactionId,
    trackError,
    setSwiperError,
    trackTiming,
  } = args

  const { id: registerId, name: registerName } = register

  return {
    fetchCNPConfigEffect: (_state, _effect, dispatch) => {
      const stopFetchConfigTimer = trackTiming(LSPAY_STEPS.CNP_FETCH_CONFIG)
      fetchCNPConfig(outletId)
        .then(cnpConfig => {
          dispatch({ type: 'CNP_CONFIG_OK', clientKey: cnpConfig.client_key })
        })
        .catch(error => {
          if (
            error.code ===
            SWIPER_ERROR_CODES.LSPAY_GET_CARD_NOT_PRESENT_CONFIG_NOT_FOUND
          ) {
            setSwiperError(error)
            return
          }

          dispatch({
            type: 'ERROR',
            errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT],
          })
          trackError(FAILED_CNP_CONFIG, error)
        })
        .finally(stopFetchConfigTimer)
    },
    createIntentEffect: (_state, _effect, dispatch) => {
      const stopCreateIntentTimer = trackTiming(LSPAY_STEPS.CNP_CREATE_INTENT)
      createPaymentIntent({
        amount,
        currency,
        outletId,
        retailerPaymentTypeId,
        registerId,
        metadata: {
          order_id: transactionId,
          register_name: registerName,
        },
      })
        .then(clientSecret => {
          dispatch({ type: 'INTENT_OK', clientSecret })
        })
        .catch(error => {
          if (error?.code === SWIPER_ERROR_CODES.LSPAY_AMOUNT_TOO_SMALL) {
            setSwiperError(error)
            return
          }

          dispatch({
            type: 'ERROR',
            errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT],
          })

          trackError(FAILED_INTENT, error)
        })
        .finally(stopCreateIntentTimer)
    },
    captureEffect: (_state, effect, dispatch) => {
      const { intentId } = effect
      const stopCaptureTimer = trackTiming(LSPAY_STEPS.CNP_CAPTURE)

      capturePayment({
        registerId,
        retailerPaymentTypeId,
        transactionId,
        data: {
          amount,
          currency,
          lspay_intent_id: effect.intentId,
          outlet_id: outletId,
          // `card` is the CNP payment method name
          lspay_payment_method: 'card',
        },
      })
        .then(transaction => {
          dispatch({ type: 'CAPTURE_OK', transaction })
        })
        .catch(error => {
          dispatch({
            type: 'ERROR',
            errorMessage: LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_PAYMENT],
          })
          trackError(FAILED_CAPTURE, error, { intentId })
        })
        .finally(stopCaptureTimer)
    },
  }
}

export const getEffectForRetry = (
  state: CardNotPresentPaymentState
): CardNotPresentPaymentEffect | undefined => {
  switch (state.status) {
    case 'fetchingCNPConfig': {
      return {
        type: 'fetchCNPConfigEffect',
      }
    }
    case 'creatingIntent': {
      return {
        type: 'createIntentEffect',
      }
    }
    case 'capturing': {
      return {
        type: 'captureEffect',
        intentId: state.intentId,
      }
    }
  }
}
